"use client"

import { FC, useState } from "react"
import { Content } from "@prismicio/client"
import { PrismicRichText, SliceComponentProps } from "@prismicio/react"
import { PrismicNextImage } from "@prismicio/next"
import {
  LazyMotion,
  domAnimation,
  m,
  motion,
  type Variants,
  useMotionValue,
  useTransform,
} from "motion/react"
import { vFadeUp, vFadeIn, vStaggerContainer, vCardPop } from "@/lib/animations"

export type CentersOverviewProps =
  SliceComponentProps<Content.CentersOverviewSlice>

const vLineGrow: Variants = {
  hidden: { scaleX: 0, opacity: 0 },
  visible: {
    scaleX: 1,
    opacity: 1,
    transition: { duration: 1, ease: "easeOut" },
  },
}

const vTagPop: Variants = {
  hidden: { opacity: 0, scale: 0.85, y: 12 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.34, 1.56, 0.64, 1] as const },
  },
}

const CentersOverview: FC<CentersOverviewProps> = ({ slice }) => {
  const { primary } = slice
  const mouseX = useMotionValue(0)
  const mouseY = useMotionValue(0)
  const spotX = useTransform(mouseX, (v) => `${v}px`)
  const spotY = useTransform(mouseY, (v) => `${v}px`)
  const [spotVisible, setSpotVisible] = useState(false)

  function handleBlockMouseMove(e: React.MouseEvent<HTMLDivElement>) {
    const rect = e.currentTarget.getBoundingClientRect()
    mouseX.set(e.clientX - rect.left)
    mouseY.set(e.clientY - rect.top)
  }

  return (
    <LazyMotion features={domAnimation}>
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        aria-label={slice.slice_type
          .split("_")
          .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ")}
        className="relative overflow-hidden section-py"
        style={{
          background:
            "linear-gradient(160deg, oklch(0.15 0.065 18) 0%, oklch(0.21 0.085 22) 40%, oklch(0.17 0.07 20) 100%)",
        }}
      >
        {/* Background texture: slowly spinning concentric rings */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 flex items-center justify-center"
        >
          {[1, 2, 3].map((n) => (
            <motion.div
              key={n}
              className="absolute rounded-full border"
              style={{
                width: `${n * 30}vw`,
                height: `${n * 30}vw`,
                borderColor: "oklch(1 0 0 / 0.04)",
              }}
              animate={{ rotate: n % 2 === 0 ? 360 : -360 }}
              transition={{
                duration: 30 + n * 12,
                repeat: Infinity,
                ease: "linear",
              }}
            />
          ))}
        </div>

        {/* Floating gold particles */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 overflow-hidden"
        >
          {[...Array(6)].map((_, n) => (
            <motion.div
              key={n}
              className="absolute size-1 rounded-full"
              style={{
                background: "var(--sacred-gold)",
                left: `${15 + n * 14}%`,
                top: `${20 + (n % 3) * 25}%`,
              }}
              animate={{ y: [-8, 8, -8], opacity: [0.15, 0.4, 0.15] }}
              transition={{
                duration: 3 + n * 0.7,
                repeat: Infinity,
                ease: "easeInOut",
                delay: n * 0.4,
              }}
            />
          ))}
        </div>

        {/* Top bloom */}
        <div
          aria-hidden
          className="pointer-events-none absolute top-0 left-1/2 size-96 -translate-1/2 rounded-full blur-3xl"
          style={{ background: "oklch(0.59 0.17 42 / 0.25)" }}
        />

        <m.div
          className="relative container flex flex-col items-center gap-10"
          variants={vStaggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
        >
          {/* Icon with halo + pulse rings */}
          {primary.icon?.url && (
            <m.div
              variants={vCardPop}
              className="relative flex items-center justify-center"
            >
              {[1, 2].map((n) => (
                <motion.div
                  key={n}
                  className="absolute rounded-full border"
                  style={{ borderColor: "var(--sacred-gold)" }}
                  initial={{ width: 80, height: 80, opacity: 0.5 }}
                  animate={{
                    width: 80 + n * 36,
                    height: 80 + n * 36,
                    opacity: 0,
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeOut",
                    delay: n * 0.7,
                  }}
                />
              ))}
              <div
                className="absolute inset-0 scale-[2.2] rounded-full blur-2xl"
                style={{ background: "var(--sacred-gold)", opacity: 0.35 }}
              />
              <div className="relative size-20 overflow-hidden rounded-full ring-2 ring-white/20 ring-offset-2 ring-offset-transparent">
                <PrismicNextImage
                  field={primary.icon}
                  fallbackAlt=""
                  width={80}
                  height={80}
                  className="size-full object-cover"
                  imgixParams={{ auto: ["format", "compress"], q: 75 }}
                />
              </div>
            </m.div>
          )}

          {/* Headline */}
          {primary.headline && (
            <m.div variants={vFadeUp} className="text-center">
              <PrismicRichText
                field={primary.headline}
                components={{
                  heading1: ({ children }) => (
                    <h1 className="text-gradient-amber">{children}</h1>
                  ),
                  heading2: ({ children }) => (
                    <h2 className="text-gradient-amber">{children}</h2>
                  ),
                  paragraph: ({ children }) => (
                    <p className="text-white/70">{children}</p>
                  ),
                }}
              />
            </m.div>
          )}

          {/* Shimmer divider */}
          <m.div
            variants={vLineGrow}
            className="h-px w-48 origin-center self-center rounded-full"
            style={{
              background:
                "linear-gradient(to right, transparent, var(--sacred-gold), transparent)",
            }}
          />

          {/* Intro */}
          {primary.intro && (
            <m.div variants={vFadeUp} className="max-w-2xl text-center">
              <PrismicRichText
                field={primary.intro}
                components={{
                  heading3: ({ children }) => (
                    <h3 className="text-white">{children}</h3>
                  ),
                  paragraph: ({ children }) => (
                    <p className="text-base/relaxed text-white/70">
                      {children}
                    </p>
                  ),
                }}
              />
            </m.div>
          )}

          {/* Highlighted info block — mouse spotlight */}
          {primary.highlight_info && (
            <m.div
              variants={vFadeUp}
              onMouseMove={handleBlockMouseMove}
              onMouseEnter={() => setSpotVisible(true)}
              onMouseLeave={() => setSpotVisible(false)}
              className="relative w-full max-w-3xl overflow-hidden rounded-3xl p-px"
              style={{
                background:
                  "linear-gradient(135deg, oklch(0.6 0.15 42 / 0.6), oklch(0.4 0.1 22 / 0.3))",
              }}
            >
              {/* Mouse spotlight */}
              <motion.div
                aria-hidden
                className="pointer-events-none absolute size-64 -translate-1/2 rounded-full blur-3xl"
                style={{
                  left: spotX,
                  top: spotY,
                  background: "oklch(0.59 0.17 42 / 0.2)",
                  opacity: spotVisible ? 1 : 0,
                  transition: "opacity 0.3s",
                }}
              />
              <div
                className="relative rounded-3xl px-8 py-6 backdrop-blur-sm"
                style={{
                  background:
                    "linear-gradient(135deg, oklch(0.22 0.09 22 / 0.95) 0%, oklch(0.18 0.07 20 / 0.95) 100%)",
                }}
              >
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 rounded-3xl"
                  style={{
                    background:
                      "radial-gradient(ellipse 80% 50% at 50% 0%, oklch(0.59 0.17 42 / 0.15) 0%, transparent 70%)",
                  }}
                />
                <PrismicRichText
                  field={primary.highlight_info}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="relative my-3 text-base/relaxed text-white/80 first:mt-0 last:mb-0">
                        {children}
                      </p>
                    ),
                    strong: ({ children }) => (
                      <strong className="text-gradient-amber font-bold">
                        {children}
                      </strong>
                    ),
                  }}
                />
              </div>
            </m.div>
          )}

          {/* Region tags */}
          {primary.regions && primary.regions.length > 0 && (
            <m.div
              variants={vStaggerContainer}
              className="flex w-full max-w-3xl flex-wrap justify-center gap-3"
            >
              {primary.regions.map((region, i) => (
                <m.span
                  key={i}
                  variants={vTagPop}
                  className="group relative cursor-default overflow-hidden rounded-full px-5 py-2.5 text-sm font-semibold text-white/90 transition-all duration-300 hover:-translate-y-0.5 hover:text-white hover:shadow-[0_4px_16px_-4px_oklch(0.59_0.17_42/0.5)]"
                  style={{
                    background:
                      "linear-gradient(135deg, oklch(0.30 0.10 28) 0%, oklch(0.24 0.08 22) 100%)",
                    boxShadow:
                      "0 0 0 1px oklch(1 0 0 / 0.1), inset 0 1px 0 oklch(1 0 0 / 0.07)",
                  }}
                >
                  {/* Hover shimmer */}
                  <span
                    aria-hidden
                    className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                    style={{
                      background:
                        "linear-gradient(135deg, oklch(0.59 0.17 42 / 0.25) 0%, transparent 70%)",
                    }}
                  />
                  <span className="relative">{region.region_label}</span>
                </m.span>
              ))}
            </m.div>
          )}

          {/* Shimmer divider bottom */}
          <m.div
            variants={vLineGrow}
            className="h-px w-48 origin-center self-center rounded-full"
            style={{
              background:
                "linear-gradient(to right, transparent, var(--sacred-gold), transparent)",
            }}
          />

          {/* Goal statement */}
          {primary.goal_statement && (
            <m.div variants={vFadeIn} className="max-w-2xl text-center">
              <PrismicRichText
                field={primary.goal_statement}
                components={{
                  paragraph: ({ children }) => (
                    <p
                      className="text-base/relaxed font-semibold italic"
                      style={{ color: "var(--sacred-gold)" }}
                    >
                      {children}
                    </p>
                  ),
                  strong: ({ children }) => (
                    <strong className="text-gradient-fire font-extrabold not-italic">
                      {children}
                    </strong>
                  ),
                }}
              />
            </m.div>
          )}
        </m.div>
      </section>
    </LazyMotion>
  )
}

export default CentersOverview
